import React, { useEffect, useState, useCallback, useMemo } from "react";
import { connect } from "react-redux";
import {
  Container, Input, Modal, ModalBody, ModalHeader,
  Badge, Card, CardBody, Spinner,
} from "reactstrap";
import DataTable from "react-data-table-component";
import InfiniteScroll from "react-infinite-scroll-component";
import {
  query, where, orderBy, limit, startAfter, getDocs,
} from "firebase/firestore";
import { getVersionedCollection } from "../../utils/firestorePaths";
import useDebounce from "../../components/useDebounce";
import PlateFormat from "../../components/PlateFormat";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import AIChatButton from "../../components/Common/AIChatButton";
import {
  ALERT_TYPES, TYPE_REASSIGNED, isAlert, alertLabel, alertDescription,
  alertSeverity, alertIcon, severityColor,
} from "../../utils/alertTypes";
import {
  normalizeReassign, similarityNote, isSuspicious, versionGte, otherPlate,
  MIN_VERSION_REASSIGN_FILTER,
} from "../../utils/reassign";

const RESULTS_PER_PAGE = 30;

// --- Mobile detection hook ---
const useIsMobile = (breakpoint = 768) => {
  const [isMobile, setIsMobile] = useState(window.innerWidth < breakpoint);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < breakpoint);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, [breakpoint]);
  return isMobile;
};

// --- Helpers ---
function formatDate(dateString) {
  if (!dateString) return "";
  const date = new Date(dateString);
  return (
    date.getDate().toString().padStart(2, "0") + "/" +
    (date.getMonth() + 1).toString().padStart(2, "0") + "/" +
    date.getFullYear() + " " +
    date.getHours().toString().padStart(2, "0") + ":" +
    date.getMinutes().toString().padStart(2, "0")
  );
}

function formatDateShort(dateString) {
  if (!dateString) return "";
  const date = new Date(dateString);
  return (
    date.getDate().toString().padStart(2, "0") + "/" +
    (date.getMonth() + 1).toString().padStart(2, "0") + " " +
    date.getHours().toString().padStart(2, "0") + ":" +
    date.getMinutes().toString().padStart(2, "0")
  );
}

// El catalogo de tipos vive en src/utils/alertTypes.js, espejo del de Python.
// Aca NO se compara contra numeros: se pregunta la severidad. Es lo que permite
// que el Desktop agregue un tipo, se distribuya, y esta pantalla lo muestre
// razonable sin haber sido deployada.
// presence: 1=Entrada, 0=Salida, 2=Evento
const SEVERITY_TEXT = { warn: "text-danger", info: "text-info", none: "text-muted" };

const directionText = (presence) =>
  presence === 1 ? "ENTRADA" : presence === 0 ? "SALIDA" : "EVENTO";

const PresenceIcon = ({ row }) => {
  const s = { fontSize: 18 };
  if (isAlert(row)) {
    return (
      <span
        className={`material-symbols-rounded ${SEVERITY_TEXT[alertSeverity(row)] || "text-info"}`}
        style={s}
        title={alertDescription(row)}
      >
        {alertIcon(row)}
      </span>
    );
  }
  if (row.presence === 1) return <span className="material-symbols-rounded text-success" style={s}>arrow_forward</span>;
  if (row.presence === 0) return <span className="material-symbols-rounded text-danger" style={s}>arrow_back</span>;
  if (row.presence === 2) return <span className="material-symbols-rounded text-primary" style={s}>flag</span>;
  return <span className="material-symbols-rounded text-muted" style={s}>help</span>;
};

const PresenceLabel = ({ row }) => {
  const icon = (name) => <span className="material-symbols-rounded me-1" style={{ fontSize: 14 }}>{name}</span>;
  if (isAlert(row)) {
    const label = alertLabel(row);
    // Tipo que ni este Monitor conoce ni el Desktop nombro. Se muestra la
    // direccion, que siempre es cierta, en vez del "ALERTA" rojo anonimo que
    // salia antes: decia que algo estaba mal sin decir que.
    if (!label) {
      return (
        <Badge color="info" style={{ fontSize: 12 }}>{icon("info")}{directionText(row.presence)}</Badge>
      );
    }
    return (
      <Badge color={severityColor(alertSeverity(row))} style={{ fontSize: 12 }} title={alertDescription(row)}>
        {icon(alertIcon(row))}{label.toUpperCase()}
      </Badge>
    );
  }
  if (row.presence === 1) return <Badge color="success" style={{ fontSize: 12 }}>{icon("arrow_forward")}ENTRADA</Badge>;
  if (row.presence === 0) return <Badge color="danger" style={{ fontSize: 12 }}>{icon("arrow_back")}SALIDA</Badge>;
  if (row.presence === 2) return <Badge color="primary" style={{ fontSize: 12 }}>{icon("flag")}EVENTO</Badge>;
  return <Badge color="secondary" style={{ fontSize: 12 }}>-</Badge>;
};

// Matricula + el rastro de la reasignacion, todo en la MISMA celda.
//
// A proposito no es una columna nueva: una columna le roba ancho a los clientes
// que no tienen camaras y que por lo tanto no van a ver una reasignacion nunca.
// Los distintivos solo existen si la fila los trae.
const ReassignBadges = ({ info, alDerecha }) => (
  <div
    style={{
      display: "flex", gap: 4, flexWrap: "wrap", marginTop: 2,
      justifyContent: alDerecha ? "flex-end" : "flex-start",
    }}
  >
    <Badge color="warning" style={{ fontSize: 10 }}>Reasignado</Badge>
    {isSuspicious(info.similarity) && (
      <Badge style={{ fontSize: 10, backgroundColor: "#e67e22" }}>No coincide</Badge>
    )}
    {!!info.ticketCanceled && (
      <Badge color="secondary" style={{ fontSize: 10 }}>Cancelado</Badge>
    )}
  </div>
);

const PlateCell = ({ row, country }) => {
  const info = normalizeReassign(row);
  const otra = otherPlate(info, row.plate);
  return (
    <div style={{ paddingTop: 4, paddingBottom: 4 }}>
      <div style={{ fontWeight: 600 }}>{row.plate ? PlateFormat(row.plate, country) : "-"}</div>
      {info.isReassigned && (
        <>
          <ReassignBadges info={info} />
          {!!otra && (
            <div style={{ fontSize: 11, color: "#888", marginTop: 2, lineHeight: 1.3 }}>
              {otra.etiqueta} {PlateFormat(otra.plate, country)}
            </div>
          )}
        </>
      )}
    </div>
  );
};

// =============================================================
// REFERENCIAS
// =============================================================
// Muestra SÓLO lo que aparece en las filas cargadas.
//
// Una leyenda fija con los 9 tipos de alerta le explicaría a un cliente sin
// cámaras qué es "lista negra" o "salida sin identificar", cosas que no va a
// ver nunca: son 15 referencias posibles y la mayoría no le aplican a nadie en
// particular. Armada desde las filas, un cliente sin cámaras ve dos ítems y uno
// con ANPR ve exactamente lo que tiene delante.
//
// Efecto secundario buscado: si aparece un ícono raro, alcanza con mirar acá.
// No hay que acordarse de nada ni pasar el mouse por encima.

const COLOR_SEVERIDAD = {
  warn: { fondo: "#f8d7da", borde: "#f5c2c7", texto: "Requiere atención" },
  info: { fondo: "#eef6fb", borde: "#cfe6f5", texto: "Informativo" },
};

const InOutLegend = ({ rows }) => {
  const [abierta, setAbierta] = useState(() => {
    // Quien ya se las sabe la cierra una vez y no la ve más. Abierta por
    // defecto: el que la necesita no tiene por qué salir a buscarla.
    try {
      return localStorage.getItem("inoutLegendClosed") !== "1";
    } catch (e) {
      return true;
    }
  });

  const toggle = () => {
    const v = !abierta;
    setAbierta(v);
    try {
      localStorage.setItem("inoutLegendClosed", v ? "0" : "1");
    } catch (e) {
      /* modo privado: la preferencia no se guarda, la leyenda igual funciona */
    }
  };

  const items = useMemo(() => {
    const iconos = new Map();
    const distintivos = new Map();
    const severidades = new Map();

    for (const row of rows) {
      if (isAlert(row)) {
        const clave = `t${row.type}`;
        const etiqueta = alertLabel(row);
        // Se guarda la fila entera, no el número: así el ícono y la etiqueta
        // salen de las mismas funciones que pintan la grilla y no pueden quedar
        // diciendo una cosa distinta.
        //
        // La condición no es sólo "si no está": de un tipo que este Monitor no
        // conoce, unas filas pueden traer el nombre resuelto desde el Desktop y
        // otras no. Quedarse con la primera que aparezca haría que la leyenda
        // dijera "Salida" cuando la fila de al lado sabe decir cómo se llama.
        if (!iconos.has(clave) || (etiqueta && !iconos.get(clave).nombrado)) {
          iconos.set(clave, {
            row,
            texto: etiqueta || directionText(row.presence),
            nombrado: !!etiqueta,
            orden: 10 + (parseInt(row.type, 10) || 0),
          });
        }
        const sev = alertSeverity(row);
        if (COLOR_SEVERIDAD[sev] && !severidades.has(sev)) severidades.set(sev, COLOR_SEVERIDAD[sev]);
      } else {
        const clave = `p${row.presence}`;
        if (!iconos.has(clave)) {
          const texto = row.presence === 1 ? "Entrada" : row.presence === 0 ? "Salida" : "Evento";
          iconos.set(clave, { row, texto, orden: row.presence === 1 ? 0 : row.presence === 0 ? 1 : 2 });
        }
      }

      const info = normalizeReassign(row);
      if (info.isReassigned && !distintivos.has("re")) {
        distintivos.set("re", { color: "warning", texto: "Reasignado", ayuda: "se corrigió a mano" });
      }
      if (info.isReassigned && isSuspicious(info.similarity) && !distintivos.has("nc")) {
        distintivos.set("nc", { fondo: "#e67e22", texto: "No coincide", ayuda: "la lectura no se parece a la matrícula" });
      }
      if (info.ticketCanceled && !distintivos.has("ca")) {
        distintivos.set("ca", { color: "secondary", texto: "Cancelado", ayuda: "el ticket quedó cancelado" });
      }
    }
    // El distintivo "Reasignado" dice lo mismo que el ícono del tipo 6, así que
    // si el tipo está en la lista sobra: la misma palabra dos veces hace pensar
    // que son dos cosas distintas.
    //
    // Va DESPUÉS del recorrido y no adentro porque adentro dependería del orden
    // de las filas: una fila reasignada que conserva otra alerta (una entrada
    // con capacidad excedida se queda con ESA, no con la 6) agregaría el
    // distintivo antes de que apareciera la primera fila de tipo 6, y quedarían
    // los dos igual. Por eso mismo el distintivo tiene que seguir existiendo:
    // puede haber reasignaciones sin una sola fila de tipo 6.
    if (iconos.has(`t${TYPE_REASSIGNED}`)) distintivos.delete("re");

    // Orden fijo: primero entrada/salida, después las alertas por número de
    // tipo. Sin esto la leyenda sale en el orden en que fueron llegando las
    // filas, así que la misma pantalla la ordena distinto según qué pasó
    // primero ese día — y al pasar de página se reacomoda sola delante del
    // usuario, que es peor que cualquier orden que uno elija.
    return {
      iconos: [...iconos.values()].sort((a, b) => a.orden - b.orden),
      distintivos: [...distintivos.values()],
      severidades: [...severidades.values()],
    };
  }, [rows]);

  // Con una sola referencia no hay nada que aclarar: una flecha verde sola no
  // necesita que le expliquen que es una entrada.
  const total = items.iconos.length + items.distintivos.length;
  if (total < 2) return null;

  return (
    <div className="mb-2" style={{ fontSize: 12 }}>
      <button
        type="button"
        onClick={toggle}
        className="btn btn-link btn-sm p-0 text-muted text-decoration-none"
        style={{ fontSize: 12 }}
      >
        <span className="material-symbols-rounded" style={{ fontSize: 15, verticalAlign: "middle" }}>
          {abierta ? "expand_less" : "expand_more"}
        </span>
        Referencias
      </button>

      {abierta && (
        <div
          className="d-flex flex-wrap align-items-center gap-3 mt-1 px-2 py-2"
          style={{ background: "#fafbfc", border: "1px solid #eef0f2", borderRadius: 6 }}
        >
          {items.iconos.map((it, i) => (
            <span key={`i${i}`} className="d-flex align-items-center" style={{ color: "#555" }}>
              <PresenceIcon row={it.row} />
              <span className="ms-1">{it.texto}</span>
            </span>
          ))}

          {items.distintivos.map((d, i) => (
            <span key={`d${i}`} className="d-flex align-items-center" style={{ color: "#555" }}>
              <Badge
                color={d.color}
                style={{ fontSize: 10, ...(d.fondo ? { backgroundColor: d.fondo } : {}) }}
              >
                {d.texto}
              </Badge>
              <span className="ms-1 text-muted">{d.ayuda}</span>
            </span>
          ))}

          {items.severidades.map((s, i) => (
            <span key={`s${i}`} className="d-flex align-items-center" style={{ color: "#555" }}>
              <span
                style={{
                  width: 22, height: 12, borderRadius: 3,
                  background: s.fondo, border: `1px solid ${s.borde}`, display: "inline-block",
                }}
              />
              <span className="ms-1 text-muted">Fila {s.texto.toLowerCase()}</span>
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

// =============================================================
// MOBILE CARD
// =============================================================
const MobileInOutCard = ({ row, onImageClick, country, onCardClick }) => {
  const reassign = normalizeReassign(row);
  const otraPlate = otherPlate(reassign, row.plate);
  return (
  <Card
    className="mb-2 shadow-sm"
    style={{
      cursor: "pointer",
      ...(isAlert(row)
        ? { borderLeft: `4px solid ${alertSeverity(row) === "warn" ? "#dc3545" : "#0dcaf0"}` }
        : {}),
    }}
    onClick={() => onCardClick(row)}
  >
    <CardBody className="py-2 px-3">
      {/* Row 1: E/S + Date + Plate */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
        <div>
          <PresenceLabel row={row} />
          <span className="ms-2" style={{ fontSize: 13, color: "#888" }}>{formatDateShort(row.date)}</span>
        </div>
        {row.plate && (
          <div style={{ textAlign: "right" }}>
            <span style={{ fontWeight: 600, fontSize: 15 }}>{PlateFormat(row.plate, country)}</span>
            {reassign.isReassigned && (
              <>
                <ReassignBadges info={reassign} alDerecha />
                {!!otraPlate && (
                  <div style={{ fontSize: 11, color: "#888", marginTop: 2 }}>
                    {otraPlate.etiqueta} {PlateFormat(otraPlate.plate, country)}
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>
      {/* Row 2: Detail */}
      {row.detail && (
        <div style={{ fontSize: 13, color: "#555", marginTop: 3 }}>{row.detail}</div>
      )}
      {/* Row 3: Category + Operator + Image */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 4 }}>
        <div style={{ fontSize: 12, color: "#999" }}>
          {row.category_name && <span className="me-2">{row.category_name}</span>}
          {row.user_fullname && <span>{row.user_fullname}</span>}
        </div>
        {row.picture && (
          <img
            src={`data:image/jpeg;base64,${row.picture}`}
            alt="img"
            style={{ width: 70, height: 48, objectFit: "cover", borderRadius: 4, cursor: "pointer" }}
            onClick={(e) => { e.stopPropagation(); onImageClick(`data:image/png;base64,${row.picture}`); }}
          />
        )}
      </div>
    </CardBody>
  </Card>
  );
};

// =============================================================
// MAIN COMPONENT
// =============================================================
const InOut = ({ currentCompany, syncVersion, versionDB }) => {
  document.title = "Entradas/Salidas | TicketControl - Monitor";
  const isMobile = useIsMobile();

  const [rows, setRows] = useState([]);
  const [lastVisible, setLastVisible] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);

  // Filters
  const [searchDate, setSearchDate] = useState("");
  const [searchPlate, setSearchPlate] = useState("");
  const debouncedDate = useDebounce(searchDate, 500);
  const debouncedPlate = useDebounce(searchPlate, 500);

  // Filtro de tipo: "all" | "alerts" | "reassigned"
  const [alertFilter, setAlertFilter] = useState("all");

  // El filtro "Reasignados" necesita `is_reassigned` como campo propio del
  // documento: Firestore no puede filtrar dentro de un string. Antes de la
  // 2.0.68 ese campo no existe, asi que la pastilla no se dibuja — un boton que
  // siempre devuelve lista vacia es peor que un boton que no esta.
  const puedeFiltrarReasignados = versionGte(versionDB, MIN_VERSION_REASSIGN_FILTER);

  // Al cambiar de estacionamiento el filtro sobrevive, pero el nuevo puede
  // estar en una version que no lo soporta.
  useEffect(() => {
    if (!puedeFiltrarReasignados && alertFilter === "reassigned") setAlertFilter("all");
  }, [puedeFiltrarReasignados, alertFilter]);

  // Una consulta que falla y una lista sin resultados se ven IGUAL si el error
  // sólo va a la consola: los dos casos terminan en cero filas. El dueño lee
  // "no hay movimientos" y se queda tranquilo cuando en realidad la pantalla no
  // pudo preguntar. Por eso el error se guarda y se muestra.
  const [queryError, setQueryError] = useState(null);

  // Image modal
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const openModal = (image) => { setSelectedImage(image); setModalOpen(true); };
  const closeModal = () => { setModalOpen(false); setSelectedImage(null); };

  // Ficha modal
  const [fichaOpen, setFichaOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const openFicha = (row) => { setSelectedRow(row); setFichaOpen(true); };
  const closeFicha = () => { setFichaOpen(false); setSelectedRow(null); };

  // Reset on filter or company change
  useEffect(() => {
    if (!currentCompany?.id) return;
    setRows([]);
    setLastVisible(null);
    setHasMore(true);
    fetchRows(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedDate, debouncedPlate, currentCompany?.id, syncVersion, alertFilter]);

  const fetchRows = useCallback(
    async (reset = false) => {
      if (!currentCompany?.id) return;
      if (loading || (!hasMore && !reset)) return;

      setLoading(true);
      if (reset) setQueryError(null);
      const inoutRef = getVersionedCollection(currentCompany.id, "inout", syncVersion);

      let constraints = [];
      // Los filtros van contra Firestore, no sobre lo ya cargado: filtrar en el
      // cliente sobre 30 filas paginadas daria resultados incompletos.
      if (alertFilter === "alerts") {
        // Antes era [1,2,3] y dejaba afuera la lista negra y la capacidad
        // excedida, que ya existian. El catalogo evita que vuelva a pasar.
        constraints.push(where("type", "in", ALERT_TYPES));
      } else if (alertFilter === "reassigned" && puedeFiltrarReasignados) {
        // Igualdad, no desigualdad: `>` obligaria a que este campo sea el
        // primer orderBy y romperia el orden por fecha. Ademas los documentos
        // sin la clave quedan excluidos solos, que es justo lo que se busca.
        constraints.push(where("is_reassigned", "==", true));
      }
      if (debouncedDate) {
        constraints.push(where("date", ">=", `${debouncedDate} 00:00:00.0000`));
        constraints.push(where("date", "<=", `${debouncedDate} 23:59:59.0000`));
      }
      if (debouncedPlate && debouncedPlate.length > 0) {
        constraints.push(where("plate", "==", debouncedPlate.trim().toUpperCase()));
      }
      constraints.push(orderBy("date", "desc"));
      if (!reset && lastVisible) {
        constraints.push(startAfter(lastVisible));
      }
      constraints.push(limit(RESULTS_PER_PAGE));

      try {
        const q = query(inoutRef, ...constraints);
        const snapshot = await getDocs(q);
        const newRows = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));

        if (snapshot.docs.length > 0) {
          setLastVisible(snapshot.docs[snapshot.docs.length - 1]);
          setRows((prev) => (reset ? newRows : [...prev, ...newRows]));
          if (snapshot.docs.length < RESULTS_PER_PAGE) setHasMore(false);
        } else {
          if (reset) setRows([]);
          setHasMore(false);
        }
      } catch (error) {
        console.error("Error fetching inout:", error);
        // failed-precondition = Firestore no tiene el índice compuesto que esta
        // combinación de filtro y orden necesita. Es un error de configuración
        // del proyecto, no del cliente ni de sus datos, y se arregla una sola
        // vez: el mensaje de Firestore trae el link directo para crearlo.
        const falta = error?.code === "failed-precondition" ||
          /index/i.test(error?.message || "");
        setQueryError(
          falta
            ? "Este filtro necesita un índice que todavía no está creado en la base. " +
              "Avisale a soporte: el detalle está en la consola del navegador."
            : "No se pudieron traer los movimientos. Revisá la conexión y volvé a intentar."
        );
        setHasMore(false);
      }
      setLoading(false);
    },
    [currentCompany?.id, syncVersion, debouncedDate, debouncedPlate, lastVisible, loading, hasMore, alertFilter, puedeFiltrarReasignados]
  );

  // Desktop columns
  const columns = [
    {
      name: "E/S",
      selector: (row) => row.presence,
      cell: (row) => <PresenceIcon row={row} />,
      width: "70px",
    },
    {
      name: "Fecha",
      selector: (row) => row.date,
      cell: (row) => formatDate(row.date),
      width: "150px",
    },
    {
      name: "Matrícula",
      selector: (row) => row.plate,
      cell: (row) => <PlateCell row={row} country={currentCompany?.country} />,
      width: "150px",
    },
    {
      name: "Detalle",
      selector: (row) => row.detail,
      wrap: true,
      grow: 3,
    },
    {
      name: "Imagen",
      selector: (row) => row.picture,
      cell: (row) =>
        row.picture ? (
          <img
            src={`data:image/jpeg;base64,${row.picture}`}
            alt="img"
            style={{ width: 90, height: "auto", cursor: "pointer", borderRadius: 3 }}
            onClick={(e) => { e.stopPropagation(); openModal(`data:image/png;base64,${row.picture}`); }}
          />
        ) : (
          <span className="text-muted" style={{ fontSize: 12 }}>-</span>
        ),
      width: "120px",
    },
    {
      name: "Categoría",
      selector: (row) => row.category_name,
      width: "140px",
    },
    {
      name: "Operador",
      selector: (row) => row.user_fullname,
      wrap: true,
      width: "180px",
    },
  ];

  const customTableStyles = {
    headRow: {
      style: { position: "sticky", top: 0, backgroundColor: "#fff", zIndex: 2, boxShadow: "0px 2px 2px rgba(0,0,0,0.1)" },
    },
  };

  const conditionalRowStyles = [
    {
      when: (row) => isAlert(row) && alertSeverity(row) === "warn",
      style: { backgroundColor: "#f8d7da", color: "#721c24" },
    },
    {
      // Informativas: se marcan, pero no con el rojo de "algo salio mal".
      // Un ingreso bloqueado por lista negra o una salida descartada son el
      // sistema funcionando; pintarlas de rojo tapa las que si son graves.
      when: (row) => isAlert(row) && alertSeverity(row) !== "warn",
      style: { backgroundColor: "#eef6fb" },
    },
  ];

  // Una lista vacia por un filtro no es lo mismo que una lista vacia porque no
  // paso nada. Sin esto, "Reasignados" sin resultados parece una pantalla rota.
  const textoSinResultados =
    alertFilter === "reassigned"
      ? "No hay movimientos reasignados en este período"
      : alertFilter === "alerts"
      ? "No hay alertas en este período"
      : "No hay resultados";

  if (!currentCompany?.id) {
    return (
      <div className="page-content">
        <Container fluid>
          <div className="text-center py-5 text-muted">
            <span className="material-symbols-rounded d-block mb-3" style={{ fontSize: 48, opacity: 0.3 }}>local_parking</span>
            <p>Selecciona un estacionamiento primero</p>
          </div>
        </Container>
      </div>
    );
  }

  return (
    <div className="page-content">
      <Container fluid>
        <Breadcrumbs title="TicketControl" breadcrumbItem="Entradas / Salidas" />

        <div className="text-muted mb-3" style={{ fontSize: 13 }}>
          <span className="material-symbols-rounded text-primary me-2" style={{ fontSize: 20 }}>swap_horiz</span>
          Registro de entradas y salidas de vehículos. Puedes filtrar por fecha, matrícula o ver solo las alertas.
        </div>

        {/* Filters bar: pills left, inputs right */}
        <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
          <div className="d-flex gap-2">
            <button
              className={`btn ${alertFilter === "all" ? "btn-primary" : "btn-light"} btn-sm`}
              style={{ borderRadius: 20, fontWeight: alertFilter === "all" ? 600 : 400 }}
              onClick={() => setAlertFilter("all")}
            >
              <span className="material-symbols-rounded me-1" style={{ fontSize: 16 }}>layers</span>Todos
            </button>
            <button
              className={`btn ${alertFilter === "alerts" ? "btn-danger" : "btn-light"} btn-sm`}
              style={{ borderRadius: 20, fontWeight: alertFilter === "alerts" ? 600 : 400 }}
              onClick={() => setAlertFilter("alerts")}
            >
              <span className="material-symbols-rounded me-1" style={{ fontSize: 16 }}>warning</span>Solo alertas
            </button>
            {puedeFiltrarReasignados && (
              <button
                className={`btn ${alertFilter === "reassigned" ? "btn-warning" : "btn-light"} btn-sm`}
                style={{ borderRadius: 20, fontWeight: alertFilter === "reassigned" ? 600 : 400 }}
                onClick={() => setAlertFilter("reassigned")}
                title="Movimientos que se corrigieron a mano porque la cámara no identificó bien al vehículo"
              >
                <span className="material-symbols-rounded me-1" style={{ fontSize: 16 }}>swap_horiz</span>Reasignados
              </button>
            )}
          </div>
          <div className="d-flex align-items-center gap-2">
            <Input
              type="date"
              bsSize="sm"
              value={searchDate}
              onChange={(e) => setSearchDate(e.target.value)}
              style={{ fontSize: 14, width: "auto" }}
            />
            <Input
              type="search"
              bsSize="sm"
              placeholder="Matrícula"
              value={searchPlate}
              onChange={(e) => setSearchPlate(e.target.value)}
              style={{ fontSize: 14, width: 140 }}
            />
            {(searchDate || searchPlate) && (
              <button
                className="btn btn-link text-danger text-decoration-none btn-sm p-0"
                onClick={() => { setSearchDate(""); setSearchPlate(""); }}
              >
                <span className="material-symbols-rounded" style={{ fontSize: 18 }}>close</span>
              </button>
            )}
            {loading && rows.length === 0 && (
              <Spinner size="sm" color="primary" />
            )}
          </div>
        </div>

        <InOutLegend rows={rows} />

        {/* Content */}
        <div style={{ overflowX: "auto" }}>
          <InfiniteScroll
            dataLength={rows.length}
            next={fetchRows}
            hasMore={hasMore}
            loader={<div className="text-center py-3 text-muted">Cargando más...</div>}
          >
            {/* MOBILE VIEW */}
            {isMobile ? (
              rows.length === 0 && !loading ? (
                <p className={`text-center py-4 ${queryError ? "text-danger" : "text-muted"}`}>
                  {queryError || textoSinResultados}
                </p>
              ) : (
                rows.map((row) => (
                  <MobileInOutCard key={row.id} row={row} onImageClick={openModal} country={currentCompany?.country} onCardClick={openFicha} />
                ))
              )
            ) : (
              /* DESKTOP VIEW */
              <div style={{ minWidth: "1000px" }}>
                <DataTable
                  columns={columns}
                  data={rows}
                  pagination={false}
                  highlightOnHover
                  pointerOnHover
                  onRowClicked={openFicha}
                  noDataComponent={
                    <div style={{ minHeight: 200, display: "flex", justifyContent: "center", alignItems: "center" }}>
                      {loading ? (
                        <Spinner color="primary" />
                      ) : queryError ? (
                        <span className="text-danger px-3 text-center">{queryError}</span>
                      ) : (
                        textoSinResultados
                      )}
                    </div>
                  }
                  customStyles={customTableStyles}
                  conditionalRowStyles={conditionalRowStyles}
                />
              </div>
            )}
          </InfiniteScroll>
        </div>

        {/* Image Modal */}
        <Modal isOpen={modalOpen} toggle={closeModal} centered size="lg">
          <ModalHeader toggle={closeModal}>Imagen</ModalHeader>
          <ModalBody className="d-flex justify-content-center align-items-center" style={{ padding: 0 }}>
            {selectedImage && (
              <img
                src={selectedImage}
                alt="imagen completa"
                style={{ objectFit: "contain", maxWidth: "100%", maxHeight: "80vh", width: "auto", height: "auto" }}
              />
            )}
          </ModalBody>
        </Modal>

        {/* AI Chat */}
        <InOutAI rows={rows} hasMore={hasMore} />

        {/* Ficha Modal */}
        <Modal isOpen={fichaOpen} toggle={closeFicha} centered size="lg">
          {selectedRow && (
            <>
              {selectedRow.picture && (
                <div style={{ width: "100%", maxHeight: "50vh", overflow: "hidden", background: "#000" }}>
                  <img
                    src={`data:image/jpeg;base64,${selectedRow.picture}`}
                    alt="captura"
                    style={{ width: "100%", maxHeight: "50vh", objectFit: "contain" }}
                  />
                </div>
              )}
              <ModalBody className="p-3">
                <div className="d-flex align-items-center gap-2 mb-3">
                  <PresenceLabel row={selectedRow} />
                  <span className="text-muted" style={{ fontSize: 13 }}>{formatDate(selectedRow.date)}</span>
                </div>

                {selectedRow.plate && (
                  <div className="mb-2">
                    <span className="material-symbols-rounded text-primary me-1" style={{ fontSize: 18 }}>directions_car</span>
                    <span style={{ fontWeight: 700, fontSize: 18 }}>{PlateFormat(selectedRow.plate, currentCompany?.country)}</span>
                  </div>
                )}

                <ReassignDetail row={selectedRow} country={currentCompany?.country} />

                {selectedRow.detail && (
                  <div className="mb-2" style={{ fontSize: 14 }}>
                    <span className="material-symbols-rounded text-muted me-1" style={{ fontSize: 16 }}>description</span>
                    {selectedRow.detail}
                  </div>
                )}

                {selectedRow.category_name && (
                  <div className="mb-2" style={{ fontSize: 14 }}>
                    <span className="material-symbols-rounded text-muted me-1" style={{ fontSize: 16 }}>category</span>
                    {selectedRow.category_name}
                  </div>
                )}

                {selectedRow.user_fullname && (
                  <div className="mb-2" style={{ fontSize: 14 }}>
                    <span className="material-symbols-rounded text-muted me-1" style={{ fontSize: 16 }}>person</span>
                    {selectedRow.user_fullname}
                  </div>
                )}

                <hr className="my-2" />
                <div className="text-muted" style={{ fontSize: 12 }}>
                  ID: {selectedRow.id}
                </div>
              </ModalBody>
            </>
          )}
        </Modal>
      </Container>
    </div>
  );
};

// ---- Bloque de auditoria de la ficha ----
// Solo aparece si la fila trae reasignacion. Un cliente sin camaras, o uno en
// una version anterior, ve la ficha exactamente igual que siempre.
const ReassignDetail = ({ row, country }) => {
  const info = normalizeReassign(row);
  if (!info.isReassigned) return null;

  const nota = similarityNote(info.similarity);
  const fila = (icono, etiqueta, valor) =>
    !valor ? null : (
      <div className="d-flex align-items-start mb-1" style={{ fontSize: 13 }}>
        <span className="material-symbols-rounded text-muted me-2" style={{ fontSize: 16 }}>{icono}</span>
        <span className="text-muted me-2" style={{ minWidth: 110 }}>{etiqueta}</span>
        <span style={{ fontWeight: 500 }}>{valor}</span>
      </div>
    );

  return (
    <div
      className="mb-3 p-2"
      style={{ background: "#fff8ec", border: "1px solid #ffe0b2", borderRadius: 6 }}
    >
      <div className="d-flex align-items-center mb-2" style={{ fontSize: 13, fontWeight: 600 }}>
        <span className="material-symbols-rounded text-warning me-1" style={{ fontSize: 18 }}>swap_horiz</span>
        Este movimiento se reasignó
      </div>

      {fila("photo_camera", "La cámara leyó", info.readPlate ? PlateFormat(info.readPlate, country) : "")}
      {fila("how_to_reg", "Quedó como", info.assignedPlate ? PlateFormat(info.assignedPlate, country) : "")}
      {fila("label", "Tipo", info.targetTypeLabel)}
      {fila("compare_arrows", "Coincidencia", info.similarityLabel)}
      {fila("person", "Lo corrigió", info.reassignedBy)}
      {fila("schedule", "Cuándo", info.reassignedAt ? formatDate(info.reassignedAt) : "")}
      {fila("confirmation_number", "Ticket de origen", info.originTicketId ? `#${info.originTicketId}` : "")}

      {!!nota && (
        <div className="mt-2 d-flex align-items-start" style={{ fontSize: 12, color: "#8a6d3b" }}>
          <span className="material-symbols-rounded me-1" style={{ fontSize: 15 }}>info</span>
          {nota}
        </div>
      )}
      {!!info.ticketCanceled && (
        <div className="mt-1" style={{ fontSize: 12, color: "#666" }}>
          El ticket que se había creado quedó cancelado.
        </div>
      )}
      {!info.readPlate && !info.reassignedBy && (
        <div className="mt-1" style={{ fontSize: 12, color: "#666" }}>
          Es un registro anterior a que se guardara el detalle: se sabe que se
          reasignó, pero no a partir de qué lectura.
        </div>
      )}
    </div>
  );
};

// ---- AI Data Summary builder ----
const PRESENCE_LABELS = { 1: "Entrada", 0: "Salida", 2: "Evento" };

const InOutAI = ({ rows, hasMore }) => {
  const aiDataSummary = useMemo(() => {
    if (!rows.length) return "";

    let out = `REGISTRO DE ENTRADAS/SALIDAS (${rows.length} registros cargados)\n\n`;

    // Counts by type
    const entries = rows.filter((r) => r.presence === 1 && (!r.type || r.type === 0));
    const exits = rows.filter((r) => r.presence === 0 && (!r.type || r.type === 0));
    const manualOpen = rows.filter((r) => r.type === 1);
    const timeExceeded = rows.filter((r) => r.type === 2);
    const noPay = rows.filter((r) => r.type === 3);
    const events = rows.filter((r) => r.presence === 2);
    const reassigned = rows.filter((r) => normalizeReassign(r).isReassigned);

    out += `RESUMEN:\n`;
    out += `- Entradas normales: ${entries.length}\n`;
    out += `- Salidas normales: ${exits.length}\n`;
    if (manualOpen.length) out += `- Aperturas manuales: ${manualOpen.length}\n`;
    if (timeExceeded.length) out += `- Tiempo excedido: ${timeExceeded.length}\n`;
    if (noPay.length) out += `- No pago: ${noPay.length}\n`;
    if (events.length) out += `- Eventos: ${events.length}\n`;
    if (reassigned.length) out += `- Reasignados (corregidos a mano): ${reassigned.length}\n`;
    out += "\n";

    // Alerts detail
    const alerts = rows.filter((r) => isAlert(r));
    if (alerts.length) {
      out += `ALERTAS (${alerts.length}):\n`;
      alerts.slice(0, 15).forEach((a) => {
        const typeLabel = alertLabel(a) || "Alerta";
        // La matricula que leyo la camara NO se manda: el resumen viaja a un
        // modelo externo y ese dato no aporta nada a las preguntas que se
        // hacen aca. Que se reasigno, si.
        const marca = normalizeReassign(a).isReassigned ? " | reasignado" : "";
        out += `- ${typeLabel} | ${a.plate || "-"} | ${formatDate(a.date)} | ${a.detail || "-"} | ${a.user_fullname || "-"}${marca}\n`;
      });
      if (alerts.length > 15) out += `- ... y ${alerts.length - 15} mas\n`;
      out += "\n";
    }

    // By category
    const byCategory = {};
    rows.forEach((r) => {
      const cat = r.category_name || "Sin categoria";
      if (!byCategory[cat]) byCategory[cat] = { entries: 0, exits: 0 };
      if (r.presence === 1) byCategory[cat].entries++;
      if (r.presence === 0) byCategory[cat].exits++;
    });
    if (Object.keys(byCategory).length > 1) {
      out += `POR CATEGORIA:\n`;
      Object.entries(byCategory).forEach(([k, v]) => {
        out += `- ${k}: ${v.entries} entradas, ${v.exits} salidas\n`;
      });
      out += "\n";
    }

    // By operator
    const byOperator = {};
    rows.forEach((r) => {
      const op = r.user_fullname || "Sin operador";
      byOperator[op] = (byOperator[op] || 0) + 1;
    });
    if (Object.keys(byOperator).length > 1) {
      out += `POR OPERADOR:\n`;
      Object.entries(byOperator).sort((a, b) => b[1] - a[1]).forEach(([k, v]) => {
        out += `- ${k}: ${v} registros\n`;
      });
      out += "\n";
    }

    // Hourly distribution
    const byHour = {};
    rows.forEach((r) => {
      if (!r.date) return;
      const h = new Date(r.date).getHours();
      if (!byHour[h]) byHour[h] = { entries: 0, exits: 0 };
      if (r.presence === 1) byHour[h].entries++;
      if (r.presence === 0) byHour[h].exits++;
    });
    const hours = Object.keys(byHour).map(Number).sort((a, b) => a - b);
    if (hours.length >= 3) {
      out += `DISTRIBUCION HORARIA:\n`;
      hours.forEach((h) => {
        const label = `${String(h).padStart(2, "0")}:00`;
        out += `- ${label}: ${byHour[h].entries} entradas, ${byHour[h].exits} salidas\n`;
      });
      out += "\n";
    }

    // Frequent plates
    const plateCounts = {};
    rows.forEach((r) => {
      if (r.plate) plateCounts[r.plate] = (plateCounts[r.plate] || 0) + 1;
    });
    const frequent = Object.entries(plateCounts).filter(([, c]) => c >= 3).sort((a, b) => b[1] - a[1]).slice(0, 10);
    if (frequent.length) {
      out += `MATRICULAS MAS FRECUENTES:\n`;
      frequent.forEach(([plate, count]) => {
        out += `- ${plate}: ${count} registros\n`;
      });
    }

    return out;
  }, [rows]);

  const aiSuggestions = useMemo(() => [
    "¿Cuantas entradas y salidas hubo?",
    "¿Hubo alertas importantes?",
    "¿Cual fue la hora pico?",
    "¿Que operador registro mas movimientos?",
    "¿Hay matriculas sospechosas o frecuentes?",
    "¿Cuantas aperturas manuales hubo?",
    "¿Hubo movimientos reasignados?",
  ], []);

  const dataScope = hasMore
    ? `Analizando los ${rows.length} registros cargados. Desplaza hacia abajo para cargar mas datos.`
    : null;

  if (!aiDataSummary) return null;
  return (
    <AIChatButton
      context="inout"
      dataSummary={aiDataSummary}
      suggestions={aiSuggestions}
      title="Asistente Entradas/Salidas"
      dataScope={dataScope}
    />
  );
};

const mapStateToProps = (state) => ({
  currentCompany: state.companies.currentCompany,
  syncVersion: state.data.syncVersion,
  // ListenerDB la carga con `version_app || version_db`, o sea la version que
  // el cliente tiene instalada AHORA.
  versionDB: state.data.versionDB,
});

export default connect(mapStateToProps)(InOut);
